import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation } from 'wouter';
import {
  LayoutDashboard,
  Archive,
  Menu,
  X,
  Settings,
  ShieldCheck,
  Command,
  ArrowLeftRight,
  Clock,
  ChevronLeft,
  LifeBuoy,
  Sparkles,
  Radio,
  CalendarCheck,
  Activity,
  Settings2,
  Eye,
} from 'lucide-react';
import {
  ProjectsIcon,
  ClaimsIcon,
  GrantsIcon,
} from '@/components/icons/index.js';
import { EdisonMark } from '@/components/icons/edison-logo.js';
import { UserButton } from '@clerk/react';
import { useAuthStore } from '@/store/auth-store.js';
import { setPreviousRoute } from '@/lib/diagnostics.js';
import { ClaimModeToggle } from '@/components/claim-mode-toggle.js';
import { useGetMyPendingDeliberations, getGetMyPendingDeliberationsQueryKey } from '@edison/api-client-react';
import { axiosInstance, setActAsToken, setOnActAsEnded } from '@edison/api-client-react';
import { useQueryClient } from '@tanstack/react-query';
import { CommandPalette } from '@/components/command-palette/index.js';
import { NotificationBell } from '@/components/ui/notification-bell.js';

const DEMO_MODE = import.meta.env['VITE_DEMO_MODE'] === 'true';

// Admin "Act as Role" — the signed act-as token is stored per-tab in sessionStorage (dies with the
// tab). The interceptor sends it as X-Act-As; the backend clamps req.user.role to the acted role.
const ACT_AS_SESSION_KEY = 'edison-act-as-token';
// Roles a tenant_admin may act as (strictly-lower, non-portal). Mirrors the backend z.enum.
const ACT_AS_ROLES = ['r_and_d_manager', 'member', 'viewer', 'developer'] as const;

type IconComponent = React.ComponentType<{ className?: string }>;

type NavEntry =
  | { href: string; label: string; icon: IconComponent; section?: string; tourTarget?: string }
  | 'divider';

// 5-area nav for manager/admin personas
const MANAGER_NAV: NavEntry[] = [
  { href: '/', label: 'Ask Edison', icon: Sparkles },
  { href: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { href: '/projects', label: 'Projects', icon: ProjectsIcon, section: 'Workspace', tourTarget: 'projects-nav' },
  { href: '/timewriting', label: 'Time', icon: Clock },
  { href: '/evidence', label: 'Evidence', icon: Archive },
  { href: '/signals', label: 'Signals', icon: Radio },
  { href: '/recap', label: 'Weekly Recap', icon: CalendarCheck, section: 'My Work' },
  { href: '/claims', label: 'Claims', icon: ClaimsIcon, tourTarget: 'claims-nav' },
  { href: '/audit-readiness', label: 'Audit Readiness', icon: ShieldCheck, tourTarget: 'audit-readiness-nav' },
  { href: '/grants', label: 'Grants', icon: GrantsIcon, section: 'Funding' },
];

// Developer persona — focused on their own work
const DEVELOPER_NAV: NavEntry[] = [
  { href: '/developer/my-work', label: 'My Work', icon: Activity, section: 'My Work' },
  { href: '/recap', label: 'Weekly Recap', icon: CalendarCheck },
  { href: '/developer/setup', label: 'My Setup', icon: Settings2 },
];


interface ShellProps {
  children: React.ReactNode;
}

// Return the section root path for a given location (used by the back-button fallback)
function getBreadcrumbRootHref(location: string): string {
  const seg = location.split('/').filter(Boolean)[0];
  return seg ? `/${seg}` : '/';
}

// Compute breadcrumb root label from the current path
function useBreadcrumbRoot(location: string): string {
  const seg = location.split('/').filter(Boolean)[0] ?? '';
  const map: Record<string, string> = {
    '': 'Home',
    dashboard: 'Dashboard',
    insights: 'Insights',
    projects: 'Projects',
    activities: 'Projects',
    timewriting: 'Time',
    timesheets: 'Time',
    'abandoned-work': 'Time',
    evidence: 'Evidence',
    artefacts: 'Evidence',
    signals: 'Signals',
    links: 'Evidence',
    claims: 'Claims',
    expenditure: 'Claims',
    'project-export': 'Claims',
    'audit-readiness': 'Audit Readiness',
    grants: 'Grants',
    settings: 'Settings',
    integrations: 'Settings',
    users: 'Settings',
    audit: 'Settings',
    profile: 'Settings',
    help: 'Help',
    about: 'About',
    recap: 'Weekly Recap',
    developer: 'Developer',
  };
  return map[seg] ?? 'Edison';
}

export function Shell({ children }: ShellProps) {
  const [location, setLocation] = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    setPreviousRoute(location);
  }, [location]);

  // Track the previous route so the back button knows whether there is in-session history.
  // The cleanup function runs when location is about to change, capturing the leaving route.
  // This ensures prevLocationRef holds the prior route when the user can click Back, not the
  // current route (which would happen if we set it in the effect body after navigation).
  const prevLocationRef = useRef<string | null>(null);
  useEffect(() => {
    return () => {
      prevLocationRef.current = location;
    };
  }, [location]);

  // Show the back button only on "deep" paths (2+ non-empty segments, e.g. /projects/abc123)
  const isDeepPath = location.split('/').filter(Boolean).length >= 2;

  const handleBack = useCallback(() => {
    // If we navigated within this session, the browser history stack has an entry to pop.
    // prevLocationRef.current is set to the previous location value during the current render
    // (the effect that updates it runs after render, so on re-render it still holds the old value).
    if (prevLocationRef.current !== null && prevLocationRef.current !== location) {
      window.history.back();
    } else {
      // No in-session history — fall back to the section root path
      setLocation(getBreadcrumbRootHref(location));
    }
  }, [location, setLocation]);

  const expanded = true;

  // Persona override — lets any user preview the other persona's nav
  const [personaOverride, setPersonaOverride] = useState<'auto' | 'developer' | 'manager'>(() => {
    try {
      return (localStorage.getItem('edison-persona-override') as 'auto' | 'developer' | 'manager') ?? 'auto';
    } catch {
      return 'auto';
    }
  });

  const { user } = useAuthStore();

  // Act-as mode (admin only). actAsRole is the EFFECTIVE role while impersonating; null otherwise.
  const queryClient = useQueryClient();
  const [actAsRole, setActAsRole] = useState<string | null>(null);

  // Boot: load any stored token into the interceptor (survives a same-tab reload; sessionStorage
  // dies with the tab). The first response clears it if expired (ACT_AS_EXPIRED → onActAsEnded).
  useEffect(() => {
    const raw = sessionStorage.getItem(ACT_AS_SESSION_KEY);
    if (raw) {
      try {
        const { token, role } = JSON.parse(raw) as { token: string; role: string };
        setActAsToken(token);
        setActAsRole(role);
      } catch {
        sessionStorage.removeItem(ACT_AS_SESSION_KEY);
      }
    }
    // Fail-closed: the axios response interceptor calls this if the backend rejects the token.
    setOnActAsEnded(() => {
      sessionStorage.removeItem(ACT_AS_SESSION_KEY);
      setActAsToken(null);
      setActAsRole(null);
      queryClient.clear();
    });
    return () => setOnActAsEnded(null);
  }, [queryClient]);

  const enterActAs = useCallback(async (targetRole: string) => {
    const { data } = await axiosInstance.post<{ data: { token: string } }>('/api/auth/act-as', { targetRole });
    const token = data.data.token;
    sessionStorage.setItem(ACT_AS_SESSION_KEY, JSON.stringify({ token, role: targetRole }));
    setActAsToken(token);
    setActAsRole(targetRole);
    queryClient.clear();  // admin-fetched data must not bleed into the impersonated view
    setLocation('/');     // re-land; a developer target will redirect to /developer/my-work
  }, [queryClient, setLocation]);

  const exitActAs = useCallback(async () => {
    setActAsToken(null);  // clear FIRST so the exit call evaluates as the real admin
    try {
      await axiosInstance.post('/api/auth/act-as/exit', { targetRole: actAsRole });
    } catch { /* exit is a local escape hatch — never block on the audit call */ }
    sessionStorage.removeItem(ACT_AS_SESSION_KEY);
    setActAsRole(null);
    queryClient.clear();
    setLocation('/');
  }, [actAsRole, queryClient, setLocation]);

  const isDeveloper =
    actAsRole === 'developer' ||
    (!actAsRole && (personaOverride === 'developer' ||
      (personaOverride === 'auto' && user?.role === 'developer')));

  const navItems = isDeveloper ? DEVELOPER_NAV : MANAGER_NAV;

  const { data: pendingDelibData } = useGetMyPendingDeliberations({
    query: { enabled: !isDeveloper, queryKey: getGetMyPendingDeliberationsQueryKey() },
  });
  const deliberationBadge = pendingDelibData?.data?.items?.length ?? 0;

  // ERA-019: the badge uses the ONE canonical pending count (/time-entries/summary totals — pending
  // status only, no_rd/archived/deleted projects excluded, same definition as the dashboard and
  // chatbot). Previously it counted a page-limited, all-scope /time-entries list, so the sidebar
  // disagreed with every other "pending" surface. No incomeYear here → all-FY outstanding review.
  const [timewritingBadge, setTimewritingBadge] = useState(0);
  useEffect(() => {
    if (isDeveloper) return;
    axiosInstance
      .get<{ data: { totals: { pendingCount: number } } }>('/api/time-entries/summary')
      .then((res) => setTimewritingBadge(res.data?.data?.totals?.pendingCount ?? 0))
      .catch(() => {});
  }, [isDeveloper]);

  const [outdatedHookCount, setOutdatedHookCount] = useState(0);
  useEffect(() => {
    axiosInstance
      .get<{ data: { items: { revokedAt: string | null; hookCurrent: boolean }[] } }>('/api/machine-registrations')
      .then(res => {
        const items = res.data?.data?.items ?? [];
        setOutdatedHookCount(items.filter(d => !d.revokedAt && !d.hookCurrent).length);
      })
      .catch(() => {});
  }, []);

  const navBadges: Record<string, number> = {};
  if (deliberationBadge > 0) navBadges['/projects'] = deliberationBadge;
  if (timewritingBadge > 0) navBadges['/timewriting'] = timewritingBadge;
  if (outdatedHookCount > 0) navBadges['/settings'] = outdatedHookCount;

  function togglePersona() {
    const next: typeof personaOverride = isDeveloper ? 'manager' : 'developer';
    setPersonaOverride(next);
    try { localStorage.setItem('edison-persona-override', next); } catch {}
  }

  const openPalette = useCallback(() => setPaletteOpen(true), []);
  const closePalette = useCallback(() => setPaletteOpen(false), []);
  const handleAskEdison = useCallback(() => { setLocation('/help'); setPaletteOpen(false); }, [setLocation]);

  // Global ⌘K / Ctrl+K shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setPaletteOpen(p => !p);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const isActive = (href: string) =>
    href === '/' ? location === '/' : location.startsWith(href);

  const breadcrumbRoot = useBreadcrumbRoot(location);

  // Sidebar gradient per design spec
  const sidebarBg = 'linear-gradient(180deg, oklch(0.262 0.048 262), oklch(0.212 0.04 260))';
  const goldBar = 'oklch(0.74 0.13 78)';

  function renderNavItem(item: Exclude<NavEntry, 'divider'>, mobile = false) {
    const active = isActive(item.href);
    const badge = navBadges[item.href];
    return (
      <Link
        key={item.href}
        href={item.href}
        title={expanded || mobile ? undefined : item.label}
        data-tour={item.tourTarget}
        onClick={mobile ? () => setMobileMenuOpen(false) : undefined}
        className={`relative flex items-center gap-3 rounded-[9px] text-sm transition-all duration-150 ${
          mobile || expanded ? 'px-3 py-2' : 'justify-center py-2'
        }`}
        style={
          active
            ? { backgroundColor: 'oklch(0.98 0.005 250 / 0.13)', color: 'oklch(0.98 0.005 250 / 0.92)', fontWeight: 500 }
            : { color: 'oklch(0.98 0.005 250 / 0.56)' }
        }
      >
        {active && (
          <span
            className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-4 rounded-r-full"
            style={{ backgroundColor: goldBar }}
            aria-hidden="true"
          />
        )}
        <item.icon className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />
        {(expanded || mobile) && <span className="truncate flex-1">{item.label}</span>}
        {badge ? (
          <span
            className={`inline-flex items-center justify-center rounded-full bg-amber-500 text-white text-[10px] font-bold leading-none shrink-0 ${
              expanded || mobile ? 'h-4 min-w-4 px-1' : 'h-3.5 min-w-3.5 px-0.5 absolute top-0.5 right-0.5'
            }`}
            aria-label={`${badge} pending`}
          >
            {badge > 99 ? '99+' : badge}
          </span>
        ) : null}
      </Link>
    );
  }

  function renderNav(mobile = false) {
    return navItems.map((item, i) => {
      if (item === 'divider') {
        return (
          <div
            key={`d-${i}`}
            className="my-2 mx-1 border-t"
            style={{ borderColor: 'oklch(0.98 0.005 250 / 0.10)' }}
          />
        );
      }
      return (
        <div key={item.href}>
          {(expanded || mobile) && item.section && (
            <p
              className="text-[10.5px] font-semibold uppercase tracking-[0.05em] px-2 pt-2 pb-1"
              style={{ color: 'oklch(0.98 0.005 250 / 0.35)' }}
            >
              {item.section}
            </p>
          )}
          {renderNavItem(item, mobile)}
        </div>
      );
    });
  }

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* ── Desktop sidebar ───────────────────────────────────────────── */}
      <aside
        className="hidden md:flex flex-col shrink-0 w-[236px]"
        style={{ background: sidebarBg, boxShadow: 'inset -1px 0 0 oklch(0.98 0.005 250 / 0.06)' }}
        aria-label="Sidebar navigation"
      >
        {/* Logo */}
        <div className="flex items-center h-[66px] px-[18px] gap-[11px] shrink-0">
          <div
            className="flex shrink-0 items-center justify-center rounded-[9px]"
            style={{
              height: 32,
              width: 32,
              background: '#0072D5',
              boxShadow: 'inset 0 1px 0 oklch(1 0 0 / 0.16), 0 2px 7px oklch(0.20 0.06 250 / 0.45)',
            }}
          >
            <EdisonMark size={18} />
          </div>
          {expanded && (
            <div className="min-w-0" style={{ lineHeight: 1.12 }}>
              <p className="font-semibold leading-none" style={{ fontSize: 15, letterSpacing: '0.01em', color: 'oklch(0.98 0.005 250 / 0.96)' }}>
                Edison
              </p>
              <p
                className="font-semibold uppercase leading-none mt-0.5"
                style={{ fontSize: 9, letterSpacing: '0.15em', color: 'oklch(0.74 0.11 80 / 0.9)' }}
              >
                R&amp;D Intelligence
              </p>
            </div>
          )}
        </div>

        {/* ⌘K search trigger */}
        <div className="px-2 pb-2 shrink-0">
          <button
            onClick={openPalette}
            aria-label="Open command palette (⌘K)"
            className={`w-full flex items-center gap-2.5 rounded-[9px] text-sm transition-all duration-150 ${
              expanded ? 'px-3 py-2' : 'justify-center py-2'
            }`}
            style={{ backgroundColor: 'oklch(0.98 0.005 250 / 0.08)', color: 'oklch(0.98 0.005 250 / 0.55)' }}
          >
            <Command className="h-4 w-4 shrink-0" aria-hidden="true" />
            {expanded && (
              <>
                <span className="flex-1 text-left text-xs">Search…</span>
                <kbd
                  className="text-[10px] font-mono px-1 py-0.5 rounded"
                  style={{ backgroundColor: 'oklch(0.98 0.005 250 / 0.10)', color: 'oklch(0.98 0.005 250 / 0.40)' }}
                >
                  ⌘K
                </kbd>
              </>
            )}
          </button>
        </div>

        {/* Main nav */}
        <nav className="flex-1 overflow-y-auto py-1 px-2 space-y-0.5">
          {renderNav()}
        </nav>

        {/* Bottom section */}
        <div className="shrink-0" style={{ borderTop: '1px solid oklch(0.98 0.005 250 / 0.10)' }}>

          {/* Help & Support shortcut */}
          <div className="px-2 pt-2">
            <button
              onClick={() => { setLocation('/help'); }}
              aria-label="Open Help & Support"
              data-tour="edison-assistant"
              className={`w-full flex items-center gap-3 rounded-[9px] text-sm text-left transition-all duration-150 ${
                expanded ? 'px-3 py-2' : 'justify-center py-2'
              }`}
              style={{ color: 'oklch(0.98 0.005 250 / 0.56)' }}
            >
              <LifeBuoy className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />
              {expanded && <span className="truncate flex-1">Help &amp; Support</span>}
            </button>
          </div>

          {/* Settings — single link */}
          <div className="px-2 pt-1">
            {renderNavItem({ href: '/settings', label: 'Settings', icon: Settings })}
          </div>

          {/* User card + persona toggle */}
          <div
            className={`py-2 ${expanded ? 'px-3' : 'px-2'}`}
            style={{ borderTop: '1px solid oklch(0.98 0.005 250 / 0.10)' }}
          >
            <div className={`flex items-center rounded-lg ${expanded ? 'gap-2 px-1 py-1.5' : 'justify-center py-1.5'}`}>
              {DEMO_MODE ? (
                <div className="h-7 w-7 rounded-full bg-indigo-500 flex items-center justify-center text-white text-xs font-bold shrink-0" aria-hidden="true">
                  JO
                </div>
              ) : (
                <UserButton />
              )}
              {expanded && (
                <>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium truncate" style={{ color: 'oklch(0.98 0.005 250 / 0.90)' }}>
                      {DEMO_MODE ? 'James Okafor' : (user?.name ?? user?.email ?? '—')}
                    </p>
                    <p className="text-[10px] truncate" style={{ color: 'oklch(0.98 0.005 250 / 0.40)' }}>
                      {isDeveloper ? 'Developer view' : 'Manager view'}
                    </p>
                  </div>
                  {!actAsRole && (
                    <button
                      onClick={togglePersona}
                      aria-label={`Switch to ${isDeveloper ? 'manager' : 'developer'} view`}
                      title={`Switch to ${isDeveloper ? 'manager' : 'developer'} view — preview changes the menu only, it does not change your access.`}
                      className="p-1 rounded transition-colors shrink-0"
                      style={{ color: 'oklch(0.98 0.005 250 / 0.35)' }}
                    >
                      <ArrowLeftRight className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  )}
                </>
              )}
            </div>
            {/* Act-as menu — tenant_admin only, and only when not already impersonating. Lets an
                admin experience Edison as a lower role, including the real backend access denials. */}
            {expanded && user?.role === 'tenant_admin' && !actAsRole && (
              <div className="px-1 pt-2">
                <span className="text-[10px] font-semibold uppercase tracking-[0.05em]" style={{ color: 'oklch(0.98 0.005 250 / 0.35)' }}>
                  Act as…
                </span>
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {ACT_AS_ROLES.map((r) => (
                    <button
                      key={r}
                      onClick={() => { void enterActAs(r); }}
                      className="text-[11px] rounded border px-2 py-0.5 transition-colors"
                      style={{ borderColor: 'oklch(0.98 0.005 250 / 0.16)', color: 'oklch(0.98 0.005 250 / 0.65)' }}
                      aria-label={`Act as ${r.replace(/_/g, ' ')}`}
                    >
                      {r.replace(/_/g, ' ')}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <p
              className={`text-[10px] font-mono select-all mt-1 ${expanded ? 'px-1' : 'text-center'}`}
              style={{ color: 'oklch(0.98 0.005 250 / 0.20)' }}
            >
              {expanded ? `v${__BUILD_SHA__} · ${__BUILD_TIME__}` : `v${__BUILD_SHA__}`}
            </p>
          </div>
        </div>
      </aside>

      {/* ── Mobile overlay ───────────────────────────────────────────── */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm md:hidden"
          aria-hidden="true"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 flex flex-col transform transition-transform duration-200 ease-out md:hidden ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ background: sidebarBg }}
        aria-label="Mobile navigation"
      >
        <div className="flex items-center justify-between h-14 px-4">
          <div className="flex items-center gap-2.5">
            <div
              className="flex shrink-0 items-center justify-center rounded-[9px]"
              style={{
                height: 28,
                width: 28,
                background: '#0072D5',
                boxShadow: 'inset 0 1px 0 oklch(1 0 0 / 0.16)',
              }}
            >
              <EdisonMark size={16} />
            </div>
            <span className="font-bold text-sm tracking-tight" style={{ color: 'oklch(0.98 0.005 250 / 0.90)' }}>
              Edison
            </span>
          </div>
          <button
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Close menu"
            className="p-1 rounded-lg transition-colors"
            style={{ color: 'oklch(0.98 0.005 250 / 0.60)' }}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-2 px-2 space-y-0.5">
          {renderNav(true)}
        </nav>

        <div style={{ borderTop: '1px solid oklch(0.98 0.005 250 / 0.10)' }}>
          <button
            onClick={() => { setLocation('/help'); setMobileMenuOpen(false); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm"
            style={{ color: 'oklch(0.98 0.005 250 / 0.56)' }}
          >
            <LifeBuoy className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />
            <span>Help &amp; Support</span>
          </button>

          <Link
            href="/settings"
            onClick={() => setMobileMenuOpen(false)}
            className="flex items-center gap-3 px-3 py-2 text-sm"
            style={
              isActive('/settings')
                ? { color: 'oklch(0.98 0.005 250 / 0.92)' }
                : { color: 'oklch(0.98 0.005 250 / 0.50)' }
            }
          >
            <Settings className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="text-xs">Settings</span>
          </Link>

          <div
            className="p-3 flex items-center gap-2"
            style={{ borderTop: '1px solid oklch(0.98 0.005 250 / 0.10)' }}
          >
            {DEMO_MODE ? (
              <div className="h-8 w-8 rounded-full bg-indigo-500 flex items-center justify-center text-white text-xs font-bold shrink-0" aria-hidden="true">
                JO
              </div>
            ) : (
              <UserButton />
            )}
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium truncate" style={{ color: 'oklch(0.98 0.005 250 / 0.90)' }}>
                {DEMO_MODE ? 'James Okafor' : (user?.name ?? user?.email ?? '—')}
              </p>
            </div>
            <button
              onClick={togglePersona}
              aria-label="Switch persona"
              className="p-1 rounded shrink-0"
              style={{ color: 'oklch(0.98 0.005 250 / 0.35)' }}
            >
              <ArrowLeftRight className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      {/* ── Main content ──────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Act-as banner — non-dismissable while impersonating a lower role (read-only mode). */}
        {actAsRole && (
          <div
            role="status"
            className="sticky top-0 z-50 flex items-center justify-between gap-3 bg-amber-500/15 border-b border-amber-500/40 px-4 py-2 text-sm text-amber-900 dark:text-amber-200"
          >
            <span className="flex items-center gap-2">
              <Eye className="h-4 w-4 shrink-0" aria-hidden="true" />
              Acting as <strong>{actAsRole.replace(/_/g, ' ')}</strong> — you are seeing Edison as this role sees it, including access denials. Read-only.
            </span>
            <button
              onClick={() => { void exitActAs(); }}
              className="rounded bg-amber-600 px-3 py-1 text-xs font-medium text-white hover:bg-amber-700 shrink-0"
            >
              Exit act-as
            </button>
          </div>
        )}
        {/* Mobile header */}
        <header className="md:hidden flex items-center h-14 px-4 border-b bg-card shrink-0">
          <button
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Open menu"
            className="p-1 rounded-lg hover:bg-secondary mr-3 transition-colors"
          >
            <Menu className="h-5 w-5" />
          </button>
          {isDeepPath && (
            <button
              onClick={handleBack}
              aria-label="Go back"
              className="p-1.5 rounded-lg hover:bg-surface-2 text-muted-foreground hover:text-foreground transition-colors mr-1"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          )}
          <span className="font-bold text-sm tracking-tight flex-1">Edison</span>
          <div className="flex items-center gap-2">
            <button
              onClick={openPalette}
              aria-label="Search"
              className="p-1.5 rounded-lg hover:bg-secondary transition-colors text-muted-foreground"
            >
              <Command className="h-4 w-4" />
            </button>
            <ClaimModeToggle />
          </div>
        </header>

        {/* Desktop top strip — 56px height per spec, breadcrumb + Claim-Mode lens + ⌘K */}
        <div className="hidden md:flex items-center h-14 px-6 border-b bg-card shrink-0 gap-3">
          {/* Back button — only on detail pages (2+ path segments) */}
          {isDeepPath && (
            <button
              onClick={handleBack}
              aria-label="Go back"
              className="p-1.5 rounded-lg hover:bg-surface-2 text-muted-foreground hover:text-foreground transition-colors mr-1"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          )}
          {/* Breadcrumb */}
          <div className="flex-1 flex items-center gap-1.5 text-sm min-w-0">
            <span className="font-medium text-foreground truncate">{breadcrumbRoot}</span>
          </div>

          {/* Right controls */}
          <div className="flex items-center gap-2 shrink-0">
            <ClaimModeToggle />
            <NotificationBell />
            <button
              onClick={openPalette}
              aria-label="Open command palette (⌘K)"
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors border border-border"
            >
              <Command className="h-3.5 w-3.5" aria-hidden="true" />
              <span className="font-mono">⌘K</span>
            </button>
          </div>
        </div>

        <main className={`flex-1 ${location === '/help' || location === '/' ? 'overflow-hidden flex flex-col' : 'overflow-y-auto'}`}>
          {location === '/help' || location === '/' ? (
            <div className="flex-1 min-h-0 animate-page overflow-y-auto flex flex-col">
              {children}
            </div>
          ) : (
            <div className="max-w-[1320px] mx-auto px-8 py-6 lg:px-10 animate-page">
              {children}
            </div>
          )}
        </main>
      </div>

      {/* ── Global overlays ──────────────────────────────────────────── */}
      <CommandPalette
        open={paletteOpen}
        onClose={closePalette}
        onAskEdison={handleAskEdison}
      />
    </div>
  );
}
